import ReactDOM from 'react-dom/client';
import { PublicClientApplication, EventType, InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import type { AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import App from './App';
import { ConnectorProvider } from './contexts/ConnectorContext';
import { SharePointConnector } from './hooks/services/sharePointConnector';
import { errorLogger } from './utils/errorLogger';
import './styles/design-system.css';
import './styles/ordering.css';

// ─── MSAL Configuration ───────────────────────────────────────────────────────

const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AAD_CLIENT_ID ?? '',
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_TENANT_ID ?? ''}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage' as const,
    storeAuthStateInCookie: false,
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

// ─── SharePoint Connector ─────────────────────────────────────────────────────

const spScope = process.env.REACT_APP_SP_SCOPE ?? '';
const siteUrl = process.env.REACT_APP_SHAREPOINT_SITE_URL ?? '';

// Serialize concurrent token requests to prevent MSAL interaction_in_progress errors
let inflightToken: Promise<string> | null = null;

const popupRedirectUri = `${window.location.origin}/auth-redirect.html`;

const getAccessToken = async (): Promise<string> => {
    if (inflightToken) return inflightToken;

    inflightToken = (async () => {
      // Prefer a cached account (covers returning users and Power Apps SSO context)
      let account = msalInstance.getActiveAccount();
      if (!account) {
        const all = msalInstance.getAllAccounts();
        if (all.length > 0) {
          account = all[0];
          msalInstance.setActiveAccount(account);
        }
      }

      if (!account) {
        const result = await msalInstance.acquireTokenPopup({ scopes: [spScope], redirectUri: popupRedirectUri });
        if (result.account) msalInstance.setActiveAccount(result.account);
        return result.accessToken;
      }

      try {
        const result = await msalInstance.acquireTokenSilent({ scopes: [spScope], account });
        return result.accessToken;
      } catch (err) {
        if (
          err instanceof InteractionRequiredAuthError ||
          (err instanceof BrowserAuthError && err.errorCode === 'monitor_window_timeout')
        ) {
          const result = await msalInstance.acquireTokenPopup({ scopes: [spScope], redirectUri: popupRedirectUri });
          if (result.account) msalInstance.setActiveAccount(result.account);
          return result.accessToken;
        }
        // If another interaction is already in progress, wait briefly and retry once
        if (err instanceof BrowserAuthError && err.errorCode === 'interaction_in_progress') {
          await new Promise(resolve => setTimeout(resolve, 500));
          const result = await msalInstance.acquireTokenSilent({ scopes: [spScope], account });
          return result.accessToken;
        }
        throw err;
      }
    })().finally(() => {
      inflightToken = null;
    });

    return inflightToken;
};

const connector = new SharePointConnector({ siteUrl, getAccessToken });
errorLogger.init({ siteUrl, getAccessToken });

// ─── Mount ────────────────────────────────────────────────────────────────────

void (async () => {
  await msalInstance.initialize();

  msalInstance.addEventCallback(event => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      if (payload.account) {
        msalInstance.setActiveAccount(payload.account);
      }
    }
  });

  if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  const root = document.getElementById('root');
  if (!root) throw new Error('Root element #root not found in document');

  ReactDOM.createRoot(root).render(
    <MsalProvider instance={msalInstance}>
      <ConnectorProvider value={connector}>
        <App />
      </ConnectorProvider>
    </MsalProvider>
  );
})();
